import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { commercialOfferService, clientService, productService } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Save, Trash2, FileText, Clock, Building2, Package, CalendarDays, StickyNote } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'da_valutare', label: 'Da valutare', cls: 'bg-slate-100 text-slate-700' },
  { value: 'in_preparazione', label: 'In preparazione', cls: 'bg-amber-100 text-amber-800' },
  { value: 'inviata', label: 'Inviata', cls: 'bg-blue-100 text-blue-800' },
  { value: 'accettata', label: 'Accettata', cls: 'bg-emerald-100 text-emerald-800' },
  { value: 'rifiutata', label: 'Rifiutata', cls: 'bg-red-100 text-red-800' },
];

const statusBadge = (s) => {
  const opt = STATUS_OPTIONS.find(o => o.value === s) || STATUS_OPTIONS[0];
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${opt.cls}`}>{opt.label}</span>;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function OfferDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [offRes, cliRes, prodRes] = await Promise.all([
        commercialOfferService.getById(id),
        clientService.getAll(),
        productService.getAll(),
      ]);
      setOffer(offRes.data);
      setClients(cliRes.data.filter(c => c.is_active !== false));
      setProducts(prodRes.data);
      setForm({
        client_id: offRes.data.client_id || '',
        request_date: offRes.data.request_date || '',
        sent_date: offRes.data.sent_date || '',
        subject: offRes.data.subject || '',
        product_id: offRes.data.product_id || '',
        status: offRes.data.status || 'da_valutare',
        notes: offRes.data.notes || '',
      });
    } catch {
      toast.error('Errore caricamento offerta');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.client_id || !form.subject || !form.request_date) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, sent_date: form.sent_date || null, product_id: form.product_id || null };
      await commercialOfferService.update(id, payload);
      toast.success('Offerta aggiornata');
      setEditing(false);
      load();
    } catch {
      toast.error('Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminare questa offerta? L\'operazione non è reversibile.')) return;
    try {
      await commercialOfferService.delete(id);
      toast.success('Offerta eliminata');
      nav('/commercial-offers');
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Caricamento...</div>;
  if (!offer) return <div className="p-8 text-center text-slate-400">Offerta non trovata</div>;

  const InfoRow = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-3 py-2.5">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
        <div className="mt-0.5 text-sm text-slate-800">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="offer-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/commercial-offers')} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="offer-number">
              <FileText className="w-5 h-5 text-slate-400" />
              {offer.offer_number}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{offer.subject}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-btn">Modifica</Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 border-red-200" onClick={handleDelete} data-testid="delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => { setEditing(false); load(); }}>Annulla</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-btn">
                <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Info Card */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 space-y-1" data-testid="offer-info-card">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Dettagli Offerta</h2>

          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Cliente *</label>
                <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid="edit-client">
                  <option value="">Seleziona...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{`${c.code} - ${c.name}`}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Oggetto *</label>
                <input type="text" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2" data-testid="edit-subject" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Data Arrivo *</label>
                  <input type="date" value={form.request_date} onChange={e => setForm(f => ({ ...f, request_date: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2" data-testid="edit-request-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Data Invio</label>
                  <input type="date" value={form.sent_date} onChange={e => setForm(f => ({ ...f, sent_date: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2" data-testid="edit-sent-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Stato</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid="edit-status">
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Prodotto</label>
                  <select value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid="edit-product">
                    <option value="">Nessuno</option>
                    {products.map(p => <option key={p.id} value={p.id}>{`${p.code} - ${p.name}`}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Note</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 resize-y" rows={3} data-testid="edit-notes" />
              </div>
            </div>
          ) : (
            <>
              <InfoRow icon={Building2} label="Cliente">
                {offer.client_name ? `${offer.client_code} - ${offer.client_name}` : offer.client_id}
              </InfoRow>
              <InfoRow icon={FileText} label="Oggetto">{offer.subject}</InfoRow>
              <InfoRow icon={CalendarDays} label="Data Arrivo Richiesta">{fmtDate(offer.request_date)}</InfoRow>
              <InfoRow icon={CalendarDays} label="Data Invio Offerta">{fmtDate(offer.sent_date)}</InfoRow>
              <InfoRow icon={Package} label="Prodotto">
                {offer.product_name ? `${offer.product_code} - ${offer.product_name}` : '-'}
              </InfoRow>
              <InfoRow icon={StickyNote} label="Note">{offer.notes || '-'}</InfoRow>
            </>
          )}
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Status Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="offer-status-card">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Stato</h2>
            <div className="flex items-center gap-3">
              {statusBadge(offer.status)}
            </div>
          </div>

          {/* Lead Time Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="offer-leadtime-card">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Lead Time</h2>
            {offer.lead_time_days !== null && offer.lead_time_days !== undefined ? (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className={`font-mono text-lg font-bold ${offer.lead_time_days <= 3 ? 'text-emerald-600' : offer.lead_time_days <= 7 ? 'text-amber-600' : 'text-red-600'}`}>
                  {offer.lead_time_days} giorni
                </span>
              </div>
            ) : (
              <p className="text-sm text-slate-400">Non calcolabile (manca data invio)</p>
            )}
          </div>

          {/* Meta Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="offer-meta-card">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Informazioni</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Creata il</span>
                <span className="text-slate-700">{fmtDate(offer.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Aggiornata il</span>
                <span className="text-slate-700">{fmtDate(offer.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
